// Library imports
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

// Local imports
import Button from 'components/Common/Button';
import Header from 'components/Common/Header';
import QuickAction from 'components/Home/QuickAction';
import Tab from 'components/Common/Tab';
import MediaCard from 'components/Home/Card';
import colors from 'constants/colors';
import routes from 'constants/routes';
import { AppDispatch, RootState } from 'redux/store';
import { GIVING_BASKET_SUB_TYPE, INACTIVE_BASKETS } from 'utils/constants';
import { PAYMENT_BASKET_SUB_TYPE } from '../../utils/constants';
import MuiLoader from 'components/Common/Loader';
import List from 'components/Common/List';
import { fetchGivingBasket } from 'redux/slices/baskets/givingBasketSilce';
import { fetchPaymentBasket } from 'redux/slices/baskets/paymentBasketSlice';
import { fetchSuspendBasket } from 'redux/slices/baskets/suspendBasketSlice';
import { defaultHeaders } from '../../appConfig';
import { BasketHeaders, BasketsTabs } from 'types/baskets';
import { GIVING_BASKETS, PAYMENT_BASKETS, SUSPEND_BASKETS } from 'utils/constants';
import { setBasketsTab } from 'redux/slices/common/appSlice';
import LoadMoreButton from 'components/Common/LoadMoreButton';
import { fetchInactiveBaskets } from 'redux/slices/baskets/inactiveBasketsSlice';
import TextInput from 'components/Common/TextInput';

// Styling
const Container = styled('div')({
    backgroundColor: '#F2F2F6',
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
});

const InnerContainer = styled('div')({
    marginBottom: 16,
    padding: 22,
});

const BasketContainer = styled('div')({
    backgroundColor: 'white',
    padding: 4,
    marginTop: 10,
});

const FieldContainer = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    padding: 6,
    marginTop: 12,
});

const Home = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const [pageNumber, setPageNumber] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [debouncedSearchText, setDebouncedSearchText] = useState('');
    const {
        currentTab,
        defaultCompanyId,
        givingBaskets,
        paymentBaskets,
        suspendBaskets,
        inactiveBaskets,
        givingBasketLoading,
        paymentBasketLoading,
        suspendBasketLoading,
        inactiveBasketLoading,
        loadMoreGiving,
        loadMorePayment,
        loadMoreSuspend,
        loadMoreInactive,
    } = useSelector((state: RootState) => ({
        currentTab: state.app.baskets?.currentTab,
        defaultCompanyId: state.user.defaultCompanyId,

        givingBaskets: state?.baskets?.givingList.activeRecords,
        paymentBaskets: state?.baskets?.paymentList.activeRecords,
        suspendBaskets: state?.baskets?.suspendList.suspendRecords,
        inactiveBaskets: state?.baskets?.inactiveList?.records,

        givingBasketLoading: state?.baskets?.givingList.loading,
        paymentBasketLoading: state?.baskets?.paymentList.loading,
        suspendBasketLoading: state?.baskets?.suspendList.loading,
        inactiveBasketLoading: state?.baskets?.inactiveList?.loading,

        loadMoreGiving: state.baskets.givingList.loadMore,
        loadMorePayment: state?.baskets?.paymentList.loadMore,
        loadMoreSuspend: state?.baskets?.suspendList.loadMore,
        loadMoreInactive: state?.baskets?.inactiveList?.loadMore,
    }));

    const updateData = useCallback(
        (pageNumber: number) => {
            if (!defaultCompanyId) return;
            let basketHeaders: BasketHeaders = { status: 'ACTIVE', subType: GIVING_BASKET_SUB_TYPE };
            const headers = { ...defaultHeaders, pageNumber };
            if (debouncedSearchText) {
                basketHeaders.name = debouncedSearchText;
            }
            switch (currentTab) {
                case PAYMENT_BASKETS:
                    basketHeaders.subType = PAYMENT_BASKET_SUB_TYPE;
                    return dispatch(fetchPaymentBasket({ ...basketHeaders, headers }));
                case SUSPEND_BASKETS:
                    basketHeaders.subType = undefined;
                    basketHeaders.status = 'SUSPEND';
                    return dispatch(fetchSuspendBasket({ ...basketHeaders, headers }));
                case INACTIVE_BASKETS:
                    return dispatch(fetchInactiveBaskets({ headers, name: debouncedSearchText }));
                default:
                    return dispatch(fetchGivingBasket({ ...basketHeaders, headers: { ...headers } }));
            }
        },
        [dispatch, currentTab, defaultCompanyId, debouncedSearchText]
    );

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearchText(searchText);
        }, 700);
        return () => {
            clearTimeout(timeoutId);
        };
    }, [searchText]);

    useEffect(() => {
        updateData(pageNumber);
    }, [pageNumber, updateData, defaultCompanyId, debouncedSearchText]);

    const setTabValue = async (currentTab: BasketsTabs) => {
        setPageNumber(0);
        setSearchText('');
        dispatch(setBasketsTab(currentTab));
    };

    const getLoading = () => {
        switch (currentTab) {
            case PAYMENT_BASKETS:
                return paymentBasketLoading;
            case SUSPEND_BASKETS:
                return suspendBasketLoading;
            case INACTIVE_BASKETS:
                return inactiveBasketLoading;
            default:
                return givingBasketLoading;
        }
    };
    const getLoadMore = () => {
        switch (currentTab) {
            case PAYMENT_BASKETS:
                return loadMorePayment;
            case SUSPEND_BASKETS:
                return loadMoreSuspend;
            case INACTIVE_BASKETS:
                return loadMoreInactive;
            default:
                return loadMoreGiving;
        }
    };

    const navigationHandler = (id: number | undefined) => {
        if (!id) {
            console.log('$$$ ID is undefined');
            return;
        }
        navigate(routes.home.basketDetails.replace(':id', id.toString()));
    };

    const handleSearch = (e: any) => {
        setPageNumber(0);
        setSearchText(e.target.value);
    };
    return (
        <Container>
            <div style={{ padding: 12, paddingBottom: 0 }}>
                <Header />
            </div>
            <InnerContainer>
                <Typography sx={{ fontSize: 22, fontWeight: 500, color: colors.darkblue }} variant="h5">
                    Quick Actions
                </Typography>
                <QuickAction />
                <BasketContainer>
                    <Tab
                        tabs={[
                            { value: GIVING_BASKETS, label: 'Giving Baskets' },
                            { value: PAYMENT_BASKETS, label: 'Payment Baskets' },
                            { value: SUSPEND_BASKETS, label: 'Suspended Baskets' },
                            { value: INACTIVE_BASKETS, label: 'Inactive Baskets' },
                        ]}
                        tabValue={currentTab}
                        setTabValue={setTabValue}
                    />
                    <FieldContainer>
                        <TextInput value={searchText} onChange={handleSearch} />
                        <Button
                            sx={{ width: { xs: '50%', sm: '30%', lg: '15%' }, fontSize: { xs: 12, sm: 16 } }}
                            disabled={!defaultCompanyId}
                            onClick={() => navigate(routes.home.basketCreate)}
                            title="Create New"
                        />
                    </FieldContainer>
                    <Stack minHeight={200}>
                        {currentTab === GIVING_BASKETS && (
                            <List data={givingBaskets} loading={getLoading()}>
                                {(event, index) => <MediaCard key={index} onClickNavigation={navigationHandler} event={event} />}
                            </List>
                        )}
                        {currentTab === PAYMENT_BASKETS && (
                            <List data={paymentBaskets} loading={getLoading()}>
                                {(event, index) => <MediaCard key={index} onClickNavigation={navigationHandler} event={event} />}
                            </List>
                        )}
                        {currentTab === SUSPEND_BASKETS && (
                            <List data={suspendBaskets} loading={getLoading()}>
                                {(event, index) => <MediaCard key={index} onClickNavigation={navigationHandler} event={event} />}
                            </List>
                        )}
                        {currentTab === INACTIVE_BASKETS && (
                            <List data={inactiveBaskets} loading={getLoading()}>
                                {(event, index) => <MediaCard key={index} onClickNavigation={navigationHandler} event={event} />}
                            </List>
                        )}
                        <MuiLoader loading={getLoading()} absolute />
                        {getLoadMore() && <LoadMoreButton onClick={() => setPageNumber(pageNumber + 1)} loading={getLoading()} />}
                    </Stack>
                </BasketContainer>
            </InnerContainer>
        </Container>
    );
};

export default Home;
